import { useStoreActions, useStoreState } from 'easy-peasy';
import PropTypes from 'prop-types';
import React, { useCallback, useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Loader } from '@ge/components/loader';
import { SortDirection } from '@ge/components/table/models/sort-direction';
import { useColumnState } from '@ge/components/table/use-column-state';

import PeopleTable from '../components/tables/people-table';
import { useInfinitePeopleTable } from '../data-hooks/use-infinitePeople-table';
import { globalPeopleCols, PeopleColumnDefs } from '../models/people-table-cols';

import GlobalAdminActionsHeader from './global-admin-actions-header';

const SORT_STATE_ID = 'admin-people';

/**
 * Available sort metrics for People.
 */
export const PeopleMetric = {
  TENANT: 'tenant.id',
  NAME: 'name',
};

const GlobalPeople = ({ handlePersonSelect, handleTenantSelect }) => {
  const { t } = useTranslation(['admin.people', 'general'], { useSuspense: false });
  const [currentSearch, setCurrentSearch] = useState({});
  const [filterDefs, setFilterDefs] = useState({});
  const { getUserRoleBasedEntityAttributes } = useStoreActions((actions) => actions.userRole);
  const { limitedUserRoles } = useStoreState((state) => state.userRole);

  const { sortDirection, sortMetric, updateSortMetric } = useColumnState({
    columnDefs: PeopleColumnDefs,
    defaultCols: globalPeopleCols,
    defaultSortMetric: PeopleMetric.TENANT,
    defaultSortDirection: SortDirection.ASC,
    sortStateId: SORT_STATE_ID,
  });

  const {
    data: { data: people = [], filterValues } = {},
    status,
    fetchNextPage,
    hasNextPage,
    allPeople,
  } = useInfinitePeopleTable({
    filters: filterDefs,
    search: currentSearch,
    sortMetric,
    sortDirection,
  });

  useEffect(() => {
    if (!limitedUserRoles) {
      getUserRoleBasedEntityAttributes();
    }
  }, [getUserRoleBasedEntityAttributes, limitedUserRoles]);

  useEffect(() => {
    if (hasNextPage) {
      fetchNextPage();
    }
  }, [allPeople, fetchNextPage, hasNextPage]);

  const handleFilter = useCallback(
    (updateDefs) => {
      setFilterDefs(updateDefs);
    },
    [setFilterDefs],
  );

  const handleFilterChange = useCallback((search) => setCurrentSearch(search), [setCurrentSearch]);

  return (
    <>
      <GlobalAdminActionsHeader />
      <h1 className="pageTitle">{t('people', 'People')}</h1>
      {status === 'loading' ? (
        <Loader />
      ) : (
        <PeopleTable
          className="people-table"
          columns={globalPeopleCols}
          people={people}
          sortAction={updateSortMetric}
          sortMetric={sortMetric}
          sortDirection={sortDirection}
          onFilter={handleFilter}
          onFilterChange={handleFilterChange}
          filterValues={filterValues}
          onTenantSelect={handleTenantSelect}
          onPersonSelect={handlePersonSelect}
        />
      )}
    </>
  );
};

GlobalPeople.propTypes = {
  handlePersonSelect: PropTypes.func,
  handleTenantSelect: PropTypes.func,
};

GlobalPeople.defaultProps = {
  handlePersonSelect: () => null,
  handleTenantSelect: () => null,
};

export default GlobalPeople;
